import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Users, Mail, LogOut, RefreshCw } from "lucide-react";
import { API_URL } from "@/lib/links";
import { adminSession } from "@/lib/adminSession";
import { SummaryCards } from "@/components/admin/SummaryCards";
import { DataTable } from "@/components/admin/DataTable";
import { DownloadsPanel } from "@/components/admin/DownloadsPanel";
import { PricingPanel } from "@/components/admin/PricingPanel";
import { TestEmailButton } from "@/components/admin/TestEmailButton";

const tabs = [
  { id: "waitlist", label: "Waitlist", icon: Users, cols: [["name", "Full name"], ["business", "Business"], ["email", "Email"], ["phone", "Phone"], ["plan", "Package"], ["created_at", "Joined"]] },
  { id: "contacts", label: "Messages", icon: Mail, cols: [["name", "Name"], ["company", "Company"], ["email", "Email"], ["phone", "Phone"], ["message", "Message"], ["created_at", "Sent"]] },
];

export default function Admin() {
  const navigate = useNavigate();
  const token = adminSession.get();
  const [tab, setTab] = useState("waitlist");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => { adminSession.clear(); navigate("/", { replace: true }); }, [navigate]);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const [{ data }, { data: sum }] = await Promise.all([
        axios.get(`${API_URL}/admin/${t}`, { headers }),
        axios.get(`${API_URL}/admin/summary`, { headers }),
      ]);
      setRows(data);
      setSummary(sum);
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [headers, logout]);

  useEffect(() => {
    if (!token) { navigate("/", { replace: true }); return; }
    load(tab);
  }, [tab, token, navigate, load]);

  const current = tabs.find((t) => t.id === tab);

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8" data-testid="admin-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-body sm:text-4xl">Dashboard</h1>
        </div>
        <div className="flex items-start gap-2">
          <TestEmailButton headers={headers} />
          <button type="button" onClick={() => load(tab)} className="btn btn-secondary !px-4 !py-2 text-sm" data-testid="admin-refresh-button"><RefreshCw size={14} /> Refresh</button>
          <button type="button" onClick={logout} className="btn btn-secondary !px-4 !py-2 text-sm" data-testid="admin-logout-button"><LogOut size={14} /> Sign out</button>
        </div>
      </div>

      <SummaryCards tabs={tabs} tab={tab} summary={summary} onSelect={setTab} />

      <PricingPanel headers={headers} />
      <DownloadsPanel headers={headers} />

      <DataTable
        tab={tab}
        cols={current.cols}
        rows={rows}
        loading={loading}
        selectable={false}
        selected={[]}
        onSelectAll={() => {}}
        onToggle={() => {}}
      />
    </div>
  );
}
